// WebSocket service for real-time price updates

class WebSocketService {
  constructor() {
    this.pricesWs = null;
    this.tickerWs = null;
    this.priceCallbacks = [];
    this.tickerCallbacks = [];
    this.reconnectAttempts = 0;
    this.maxReconnectAttempts = 5;
    this.reconnectDelay = 3000;
  }

  getWsUrl() {
    const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
    // Convert HTTP URL to WebSocket URL
    if (!backendUrl) {
      return 'ws://localhost:8001/api/trading';
    }
    const wsProtocol = backendUrl.startsWith('https') ? 'wss' : 'ws';
    // Remove protocol and trailing /api if present
    let wsHost = backendUrl.replace(/^https?:\/\//, '');
    // The backend URL might not have /api at the end
    return `${wsProtocol}://${wsHost}/api/trading`;
  }

  // Connect to prices WebSocket
  connectPrices(onMessage, onError) {
    if (this.pricesWs && this.pricesWs.readyState === WebSocket.OPEN) {
      return;
    }

    const wsUrl = `${this.getWsUrl()}/ws/prices`;
    console.log('Connecting to prices WebSocket:', wsUrl);

    try {
      this.pricesWs = new WebSocket(wsUrl);

      this.pricesWs.onopen = () => {
        console.log('Prices WebSocket connected');
        this.reconnectAttempts = 0;
      };

      this.pricesWs.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          if (onMessage) onMessage(data);
          this.priceCallbacks.forEach(cb => cb(data));
        } catch (e) {
          console.error('Failed to parse prices message:', e);
        }
      };

      this.pricesWs.onerror = (error) => {
        console.error('Prices WebSocket error:', error);
        if (onError) onError(error);
      };

      this.pricesWs.onclose = () => {
        console.log('Prices WebSocket closed');
        this.handleReconnect('prices', onMessage, onError);
      };
    } catch (error) {
      console.error('Failed to create prices WebSocket:', error);
    }
  }

  // Connect to ticker WebSocket for a specific symbol
  connectTicker(symbol, onMessage, onError) {
    if (this.tickerWs) {
      this.tickerWs.close();
    }

    const wsUrl = `${this.getWsUrl()}/ws/ticker/${symbol}`;
    console.log('Connecting to ticker WebSocket:', wsUrl);

    try {
      this.tickerWs = new WebSocket(wsUrl);

      this.tickerWs.onopen = () => {
        console.log(`Ticker WebSocket connected for ${symbol}`);
        this.reconnectAttempts = 0;
      };

      this.tickerWs.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          if (onMessage) onMessage(data);
          this.tickerCallbacks.forEach(cb => cb(data));
        } catch (e) {
          console.error('Failed to parse ticker message:', e);
        }
      };

      this.tickerWs.onerror = (error) => {
        console.error('Ticker WebSocket error:', error);
        if (onError) onError(error);
      };

      this.tickerWs.onclose = () => {
        console.log('Ticker WebSocket closed');
      };
    } catch (error) {
      console.error('Failed to create ticker WebSocket:', error);
    }
  }

  handleReconnect(type, onMessage, onError) {
    if (this.reconnectAttempts < this.maxReconnectAttempts) {
      this.reconnectAttempts++;
      console.log(`Attempting to reconnect ${type} WebSocket (attempt ${this.reconnectAttempts})`);
      
      setTimeout(() => {
        if (type === 'prices') {
          this.connectPrices(onMessage, onError);
        }
      }, this.reconnectDelay);
    }
  }

  // Subscribe to price updates
  onPriceUpdate(callback) {
    this.priceCallbacks.push(callback);
    return () => {
      this.priceCallbacks = this.priceCallbacks.filter(cb => cb !== callback);
    };
  }

  // Subscribe to ticker updates
  onTickerUpdate(callback) {
    this.tickerCallbacks.push(callback);
    return () => {
      this.tickerCallbacks = this.tickerCallbacks.filter(cb => cb !== callback);
    };
  }

  // Disconnect all WebSockets
  disconnect() {
    if (this.pricesWs) {
      this.pricesWs.close();
      this.pricesWs = null;
    }
    if (this.tickerWs) {
      this.tickerWs.close();
      this.tickerWs = null;
    }
    this.priceCallbacks = [];
    this.tickerCallbacks = [];
  }

  // Check connection status
  isConnected() {
    return {
      prices: this.pricesWs && this.pricesWs.readyState === WebSocket.OPEN,
      ticker: this.tickerWs && this.tickerWs.readyState === WebSocket.OPEN,
    };
  }
}

// Singleton instance
const websocketService = new WebSocketService();

export default websocketService;
